(() => {
  'use strict';

  // Calendar conversion and roundtrip policy reused from the blind design.
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Amsterdam', calendar: 'iso8601', numberingSystem: 'latn',
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit',
    minute: '2-digit', second: '2-digit', hourCycle: 'h23'
  });
  const dayFmt = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', weekday: 'long' });
  const dateFmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC', day: 'numeric', month: 'long', year: 'numeric'
  });
  function parts(ms) {
    const p = {};
    for (const x of fmt.formatToParts(new Date(ms))) {
      if (x.type !== 'literal') p[x.type] = Number(x.value);
    }
    return p;
  }
  function wall(p) {
    return Date.UTC(p.year, p.month - 1, p.day, p.hour || 0, p.minute || 0, p.second || 0);
  }
  function key(p) {
    return [p.year, String(p.month).padStart(2, '0'), String(p.day).padStart(2, '0')].join('-');
  }
  function civil(p, n = 0) {
    const d = new Date(Date.UTC(p.year, p.month - 1, p.day + n, 12));
    return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1,
      day: d.getUTCDate(), weekday: d.getUTCDay(), date: d };
  }
  function deadline(p, cutoff) {
    const [hour, minute] = cutoff.split(':').map(Number);
    const nominal = wall({ ...p, hour, minute, second: 0 });
    const offsets = new Set([-36, 0, 36].map(h => {
      const sample = nominal + h * 3600000;
      return wall(parts(sample)) - sample;
    }));
    const matches = [...offsets].map(offset => nominal - offset)
      .filter(ms => wall(parts(ms)) === nominal);
    return matches.length ? Math.min(...matches) : null;
  }
  function countdownText(end, now, format = 'clock') {
    const seconds = Math.max(0, Math.ceil((end - now) / 1000));
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor(seconds / 60) % 60;
    if (format === 'words') return seconds < 60 ? '<1m' : `${hours}h ${minutes}m`;
    return [hours, minutes, seconds % 60].map(n => String(n).padStart(2, '0')).join(':');
  }
  function validate(c) {
    if (!c || typeof c !== 'object') throw new Error('Missing configuration.');
    if (c.error) throw new Error(c.error);
    const limits = { cutoff: 5, holidays: 4096, countries: 512, allowed: 2048,
      prefix: 60, before: 200, after: 200, previewAt: 30 };
    for (const [field, limit] of Object.entries(limits)) {
      if (typeof c[field] !== 'string' || c[field].length > limit) {
        throw new Error(`Invalid ${field}: maximum ${limit} characters.`);
      }
    }
    if (!['preview', 'live'].includes(c.mode)) throw new Error('Invalid mode.');
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(c.cutoff)) throw new Error('Use a cutoff in HH:MM.');
    if (!Array.isArray(c.days) || c.days.length !== 7 || c.days.some(x => typeof x !== 'boolean') || !c.days.some(Boolean)) {
      throw new Error('Select at least one dispatch day.');
    }
    for (const value of [c.before, c.after]) {
      if (!value.trim()) throw new Error('Both copy fields are required.');
      if ((value.match(/\[countdown\]/g) || []).length > 1) throw new Error('Use [countdown] at most once per copy field.');
      if (/[\[\]]/.test(value.replace(/\[(?:countdown|day|date|cutoff)\]/g, ''))) {
        throw new Error('Unknown copy token. Use [countdown], [day], [date] or [cutoff].');
      }
    }
    const dates = c.holidays.trim() ? c.holidays.trim().split(/[\s,]+/) : [];
    for (const date of dates) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) throw new Error('Use closure dates YYYY-MM-DD.');
      const [year, month, day] = date.split('-').map(Number);
      if (year < 1970 || key(civil({ year, month, day })) !== date) throw new Error('Invalid closure date: ' + date);
    }
    const ids = c.allowed.trim() ? c.allowed.trim().split(/[\s,]+/) : [];
    const countries = c.countries.trim() ? c.countries.toUpperCase().split(',').map(x => x.trim()) : [];
    if (c.mode === 'live' && ids.some(id => !/^\d+$/.test(id))) throw new Error('Use numeric variant IDs.');
    if (c.mode === 'live' && countries.some(code => !/^[A-Z]{2}$/.test(code))) throw new Error('Use comma-separated ISO country codes.');
    if (!['left', 'center', 'right'].includes(c.align)) throw new Error('Invalid alignment.');
    if (!['clock', 'words'].includes(c.format)) throw new Error('Invalid countdown format.');
    let previewBase = null;
    if (c.mode === 'preview' && c.previewAt) {
      const ms = Date.parse(c.previewAt);
      if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/.test(c.previewAt) || !Number.isFinite(ms) ||
          new Date(ms).toISOString().replace('.000Z', 'Z') !== c.previewAt) {
        throw new Error('Preview time must be YYYY-MM-DDTHH:MM:SSZ.');
      }
      previewBase = ms;
    }
    return { ...c, holidaysSet: new Set(dates), allowedSet: new Set(ids), countriesSet: new Set(countries), previewBase };
  }
  function computeState(nowMs, cfg) {
    if (!Number.isFinite(nowMs)) throw new Error('Invalid current time.');
    const c = validate(cfg);
    const today = civil(parts(nowMs));
    for (let n = 0; n <= 370; n++) {
      const d = civil(today, n);
      if (!c.days[d.weekday] || c.holidaysSet.has(key(d))) continue;
      const end = deadline(d, c.cutoff);
      if (end === null || end <= nowMs) continue;
      const state = n === 0 ? 'today' : 'next';
      const fields = { day: n === 0 ? 'today' : n === 1 ? 'tomorrow' : dayFmt.format(d.date),
        date: dateFmt.format(d.date), cutoff: c.cutoff, countdown: countdownText(end, nowMs, c.format) };
      const template = state === 'today' ? c.before : c.after;
      const segments = [];
      if (c.prefix.trim()) segments.push({ text: c.prefix.trim() + ' · ' });
      for (const part of template.split(/(\[(?:countdown|day|date|cutoff)\])/g)) {
        const token = part.slice(1, -1);
        if (part === '[countdown]') segments.push({ timer: true, text: fields.countdown });
        else segments.push({ text: part[0] === '[' && Object.hasOwn(fields, token) ? fields[token] : part });
      }
      return { state, end, dispatchDate: key(d), today: key(today), ...fields,
        seconds: Math.max(0, Math.ceil((end - nowMs) / 1000)), segments,
        copy: segments.map(s => s.text).join('') };
    }
    throw new Error('No dispatch day found within 370 days.');
  }

  if (typeof customElements === 'undefined') {
    globalThis.EykDispatchCore = { computeState, validate, countdownText, deadline, parts, civil, key };
    return;
  }
  const TAG = 'eyk-dispatch-timer';
  if (customElements.get(TAG)) return;
  const instances = new Set();
  let interval;
  let listening = false;
  function reconcile() {
    const running = !document.hidden && [...instances].some(instance => !instance.hidden);
    if (!running && interval !== undefined) { clearInterval(interval); interval = undefined; }
    if (running && interval === undefined) interval = setInterval(refresh, 1000);
    if (!instances.size && listening) {
      document.removeEventListener('visibilitychange', sync);
      document.removeEventListener('change', sync, true);
      window.removeEventListener('pageshow', sync);
      listening = false;
    }
  }
  function refresh() {
    if (!document.hidden) {
      const now = Date.now();
      for (const instance of instances) {
        try { instance.tick(now); } catch (error) { instance.fail(error.message); }
      }
    }
    reconcile();
  }
  function sync() { refresh(); }
  function listen() {
    if (listening) return;
    document.addEventListener('visibilitychange', sync);
    document.addEventListener('change', sync, true);
    window.addEventListener('pageshow', sync);
    listening = true;
  }
  class EykDispatchTimer extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected || this.ready) return;
        this.ready = true;
        this.row = this.closest('.lightly-spaced-row');
        this.copy = this.querySelector('[data-copy]');
        this.note = this.querySelector('[data-preview]');
        this.editor = window.Shopify?.designMode === true;
        this.startedAt = Date.now();
        this.state = null;
        this.signature = null;
        this.warned = false;
        try {
          const raw = JSON.parse(this.dataset.config);
          this.preview = raw.mode === 'preview';
          const role = window.Shopify?.theme?.role;
          if (this.preview && !this.editor && !(typeof role === 'string' && role && role !== 'main')) {
            this.show(false);
            return;
          }
          this.c = validate(raw);
          this.dataset.align = this.c.align;
          this.note.hidden = true;
          this.note.textContent = '';
          const section = this.closest('[data-section-id], .shopify-section');
          if (section && !this.preview) {
            this.observer = new MutationObserver(records => {
              if (records.some(r => r.target.closest?.('form.js-product-form') ||
                  [...r.addedNodes, ...r.removedNodes].some(n => n.matches?.('form.js-product-form') || n.querySelector?.('form.js-product-form')))) sync();
            });
            this.observer.observe(section, { subtree: true, childList: true, attributes: true,
              attributeFilter: ['value', 'disabled', 'aria-disabled'] });
          }
          instances.add(this);
          listen();
          sync();
        } catch (error) { this.fail(error.message); }
      });
    }
    disconnectedCallback() {
      this.ready = false;
      this.observer?.disconnect();
      this.observer = null;
      instances.delete(this);
      reconcile();
    }
    show(visible) {
      this.hidden = !visible;
      if (this.row) this.row.hidden = !visible;
    }
    fail(message) {
      instances.delete(this);
      this.observer?.disconnect();
      if (this.copy) this.copy.textContent = '';
      if (this.preview && this.editor && this.note) {
        this.note.textContent = 'Preview — ' + message;
        this.note.hidden = false;
        this.show(true);
      } else {
        if (this.note) { this.note.textContent = ''; this.note.hidden = true; }
        this.show(false);
        if (!this.warned) { console.warn('EYEKØNIK dispatch countdown: ' + message); this.warned = true; }
      }
      reconcile();
    }
    eligible() {
      if (this.preview) return true;
      const section = this.closest('[data-section-id], .shopify-section');
      const form = section?.querySelector('form.js-product-form');
      const input = form?.querySelector('input[name="id"]');
      const submit = form?.querySelector('[type="submit"]');
      const plan = form?.querySelector('[name="selling_plan"]');
      return this.c.confirmed === true && this.c.countriesSet.has(this.c.country) &&
        this.c.eligible === true && this.c.allowedSet.has(this.c.variant) &&
        input?.value === this.c.variant && submit && !submit.disabled &&
        submit.getAttribute('aria-disabled') !== 'true' && !plan?.value;
    }
    tick(realNow) {
      if (!this.eligible()) { this.show(false); return; }
      const now = this.c.previewBase === null ? realNow : this.c.previewBase + realNow - this.startedAt;
      const today = key(parts(now));
      if (!this.state || today !== this.state.today || now >= this.state.end || now < this.lastNow) {
        this.state = computeState(now, this.c);
      }
      this.lastNow = now;
      const signature = this.state.state + ':' + this.state.dispatchDate + ':' + this.state.day;
      if (signature !== this.signature) {
        this.clock = null;
        this.copy.replaceChildren();
        for (const segment of this.state.segments) {
          if (segment.timer) {
            this.clock = document.createElement('span');
            this.clock.className = 'eyk-dispatch-timer__clock';
            this.clock.setAttribute('role', 'timer');
            this.clock.setAttribute('aria-live', 'off');
            this.copy.append(this.clock);
          } else this.copy.append(document.createTextNode(segment.text));
        }
        this.signature = signature;
      }
      if (this.clock) {
        const text = countdownText(this.state.end, now, this.c.format);
        if (this.clock.textContent !== text) this.clock.textContent = text;
      }
      this.show(true);
    }
  }
  customElements.define(TAG, EykDispatchTimer);
})();
